import { Injectable } from '@angular/core';
import { map, Observable, of, Subject, tap } from 'rxjs';

import { ApiEndpoints } from '../constants/api-endpoints';
import { ApiService } from './api.service';

export interface SubscriptionContactInfo {
  mobileWhatsapp: string[];
  email: string[];
}

export interface SubscriptionPackage {
  id: number;
  packageName: string;
  totalFlat: number;
  totalRenter: number;
  maxActiveEmployee: number;
  maxFileMemory: number;
  priceMonthly: number;
  priceYearly: number;
  status: number;
}

export interface SubscriptionInfo {
  contactInfo: SubscriptionContactInfo;
  packages: SubscriptionPackage[];
}

export interface SubscriptionCurrentPackage {
  id?: number;
  userId?: number;
  packageName?: string;
  price: number;
  maxActiveFlat: number;
  maxActiveRenter: number;
  maxActiveEmployee: number;
  maxFileStorage: number;
  isTrial?: boolean;
  expireDate?: string;
  durationUnite?: number;
  durationQuantity?: number;
  remarks?: string;
}

export interface SubscriptionUsageSummary {
  accountId: number;
  currentPackage: SubscriptionCurrentPackage;
  usage: {
    activeFlatCount: number;
    activeRenterCount: number;
    activeEmployeeCount: number;
    usedFileSize: number;
  };
}

export interface SubscriptionPackageListResponse {
  code?: string;
  message?: string;
  totalRows: number;
  data: SubscriptionPackage[];
}

export interface SubscriptionPackageHistoryItem {
  id: number;
  packageId: number;
  packageName: string;
  totalFlat: number;
  totalRenter: number;
  maxActiveEmployee: number;
  maxFileMemory: number;
  priceMonthly: number;
  priceYearly: number;
  status: number;
  actionStatus?: string;
  createdBy?: number;
  createdOn?: string;
}

export interface SubscriptionPackageHistoryListResponse {
  code?: string;
  message?: string;
  totalRows: number;
  data: SubscriptionPackageHistoryItem[];
}

export interface AssignPackageRecord {
  id: number;
  userId: number;
  packageName: string;
  price: number;
  maxActiveFlat: number;
  maxActiveRenter: number;
  maxActiveEmployee: number;
  maxFileStorage: number;
  isTrial?: boolean;
  remarks?: string;
  expireDate?: string;
  durationUnite: number;
  durationQuantity: number;
  status: number;
  createdBy?: number;
  createdOn?: string;
  updatedBy?: number;
  updatedOn?: string;
  appUser?: {
    id: number;
    name?: string;
    organizationName?: string;
    mobileNumber?: string;
    email?: string;
    username?: string;
    role?: number;
  };
  createdByUser?: {
    id: number;
    name?: string;
    organizationName?: string;
    mobileNumber?: string;
    email?: string;
    username?: string;
  };
  updatedByUser?: {
    id: number;
    name?: string;
    organizationName?: string;
    mobileNumber?: string;
    email?: string;
    username?: string;
  };
}

export interface AssignPackageListResponse {
  code?: string;
  message?: string;
  totalRows: number;
  data: AssignPackageRecord[];
}

export interface AssignPackageHistoryItem {
  id: number;
  assignPackageId: number;
  userId: number;
  packageName: string;
  price: number;
  maxActiveFlat: number;
  maxActiveRenter: number;
  maxActiveEmployee: number;
  maxFileStorage: number;
  isTrial?: boolean;
  remarks?: string;
  expireDate?: string;
  durationUnite: number;
  durationQuantity: number;
  status: number;
  actionStatus?: string;
  createdBy?: number;
  createdOn?: string;
  updatedBy?: number;
  appUser?: {
    id: number;
    name?: string;
    organizationName?: string;
    mobileNumber?: string;
    email?: string;
    username?: string;
  };
  createdByUser?: {
    id: number;
    name?: string;
    organizationName?: string;
    mobileNumber?: string;
    email?: string;
    username?: string;
  };
  updatedByUser?: {
    id: number;
    name?: string;
    organizationName?: string;
    mobileNumber?: string;
    email?: string;
    username?: string;
  };
}

export interface AssignPackageHistoryListResponse {
  code?: string;
  message?: string;
  totalRows: number;
  data: AssignPackageHistoryItem[];
}

const SUBSCRIPTION_CONTACT_CACHE_KEY = 'htally_subscription_contact_info_v2';

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private readonly summaryRefreshSubject = new Subject<void>();

  constructor(private readonly api: ApiService) {}

  get summaryRefresh$(): Observable<void> {
    return this.summaryRefreshSubject.asObservable();
  }

  getSubscriptionInfo(): Observable<SubscriptionInfo> {
    return this.api.postHandler(ApiEndpoints.SUBSCRIPTION_INFO, {}).pipe(
      map((response) => {
        const contactInfo = this.normalizeContactInfo(response?.data?.contactInfo);
        const packages = this.normalizePackages(response?.data?.packages, false);
        return { contactInfo, packages };
      }),
      tap((info) => this.writeCache(info.contactInfo))
    );
  }

  getSubscriptionContactInfo(forceRefresh = false): Observable<SubscriptionContactInfo> {
    if (!forceRefresh) {
      const cached = this.readCache();
      if (cached && this.hasAnyContact(cached)) {
        return of(cached);
      }
    }

    return this.getSubscriptionInfo().pipe(
      map((info) => info.contactInfo)
    );
  }

  getSubscriptionSummary(): Observable<SubscriptionUsageSummary> {
    return this.api.postHandler(ApiEndpoints.SUBSCRIPTION_SUMMARY, {}).pipe(
      map((response) => this.normalizeSubscriptionSummary(response?.data))
    );
  }

  requestSummaryRefresh(): void {
    this.summaryRefreshSubject.next();
  }

  listPackages(payload: {
    filters?: Record<string, any>;
    pagination?: boolean;
    pageNo?: number;
    rowCount?: number;
    excludeCustom?: boolean;
  } = {}): Observable<SubscriptionPackageListResponse> {
    return this.api.postHandler(ApiEndpoints.PACKAGE_LIST, {
      pagination: payload.pagination ?? true,
      pageNo: payload.pageNo ?? 0,
      rowCount: payload.rowCount ?? 20,
      filters: payload.filters ?? {}
    }).pipe(
      map((res) => ({
        code: res?.code,
        message: res?.message,
        totalRows: Number(res?.totalRows ?? 0),
        data: this.normalizePackages(res?.data, payload.excludeCustom ?? false)
      }))
    );
  }

  createPackage(payload: {
    packageName: string;
    totalFlat: number;
    totalRenter: number;
    maxActiveEmployee: number;
    maxFileMemory: number;
    priceMonthly: number;
    priceYearly: number;
    status?: number;
  }): Observable<any> {
    return this.api.postHandler(ApiEndpoints.PACKAGE_CREATE, payload);
  }

  updatePackage(payload: {
    id?: number;
    packageName: string;
    totalFlat: number;
    totalRenter: number;
    maxActiveEmployee: number;
    maxFileMemory: number;
    priceMonthly: number;
    priceYearly: number;
    status?: number;
  }): Observable<any> {
    return this.api.postHandler(ApiEndpoints.PACKAGE_UPDATE, payload);
  }

  deletePackage(id: number): Observable<any> {
    return this.api.postHandler(ApiEndpoints.PACKAGE_DELETE, { id });
  }

  listPackageHistory(payload: {
    filters?: Record<string, any>;
    pagination?: boolean;
    pageNo?: number;
    rowCount?: number;
  } = {}): Observable<SubscriptionPackageHistoryListResponse> {
    return this.api.postHandler(ApiEndpoints.PACKAGE_HISTORY_LIST, {
      pagination: payload.pagination ?? true,
      pageNo: payload.pageNo ?? 0,
      rowCount: payload.rowCount ?? 50,
      filters: payload.filters ?? {}
    }).pipe(
      map((res) => ({
        code: res?.code,
        message: res?.message,
        totalRows: Number(res?.totalRows ?? 0),
        data: this.normalizePackageHistory(res?.data)
      }))
    );
  }

  listAssignPackages(payload: {
    filters?: Record<string, any>;
    pagination?: boolean;
    pageNo?: number;
    rowCount?: number;
  } = {}): Observable<AssignPackageListResponse> {
    return this.api.postHandler(ApiEndpoints.ASSIGN_PACKAGE_LIST, {
      pagination: payload.pagination ?? true,
      pageNo: payload.pageNo ?? 0,
      rowCount: payload.rowCount ?? 20,
      filters: payload.filters ?? {}
    }).pipe(
      map((res) => ({
        code: res?.code,
        message: res?.message,
        totalRows: Number(res?.totalRows ?? 0),
        data: this.normalizeAssignPackages(res?.data)
      }))
    );
  }

  listAssignPackageHistory(payload: {
    filters?: Record<string, any>;
    pagination?: boolean;
    pageNo?: number;
    rowCount?: number;
  } = {}): Observable<AssignPackageHistoryListResponse> {
    return this.api.postHandler(ApiEndpoints.ASSIGN_PACKAGE_HISTORY_LIST, {
      pagination: payload.pagination ?? true,
      pageNo: payload.pageNo ?? 0,
      rowCount: payload.rowCount ?? 50,
      filters: payload.filters ?? {}
    }).pipe(
      map((res) => ({
        code: res?.code,
        message: res?.message,
        totalRows: Number(res?.totalRows ?? 0),
        data: this.normalizeAssignPackageHistory(res?.data)
      }))
    );
  }

  createAssignPackage(payload: {
    userId: number;
    packageName: string;
    price: number;
    maxActiveFlat: number;
    maxActiveRenter: number;
    maxActiveEmployee: number;
    maxFileStorage: number;
    isTrial?: boolean;
    remarks?: string | null;
    expireDate?: string | null;
    durationUnite: number;
    durationQuantity: number;
    status?: number;
  }): Observable<any> {
    return this.api.postHandler(ApiEndpoints.ASSIGN_PACKAGE_CREATE, payload);
  }

  updateAssignPackage(payload: {
    id: number;
    userId: number;
    packageName: string;
    price: number;
    maxActiveFlat: number;
    maxActiveRenter: number;
    maxActiveEmployee: number;
    maxFileStorage: number;
    isTrial?: boolean;
    remarks?: string | null;
    expireDate?: string | null;
    durationUnite: number;
    durationQuantity: number;
    status?: number;
  }): Observable<any> {
    return this.api.postHandler(ApiEndpoints.ASSIGN_PACKAGE_UPDATE, payload);
  }

  deleteAssignPackage(id: number): Observable<any> {
    return this.api.postHandler(ApiEndpoints.ASSIGN_PACKAGE_DELETE, { id });
  }

  sendAssignPackageReceiptByEmail(payload: {
    assignPackageId: number;
    email: string;
    pdfBase64: string;
    fileName: string;
  }): Observable<any> {
    return this.api.postHandler(ApiEndpoints.ASSIGN_PACKAGE_RECEIPT_SEND_EMAIL, payload);
  }

  private readCache(): SubscriptionContactInfo | null {
    try {
      const raw = localStorage.getItem(SUBSCRIPTION_CONTACT_CACHE_KEY);
      if (!raw) {
        return null;
      }
      return this.normalizeContactInfo(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  private writeCache(contactInfo: SubscriptionContactInfo): void {
    try {
      localStorage.setItem(SUBSCRIPTION_CONTACT_CACHE_KEY, JSON.stringify(contactInfo));
    } catch {
      // ignore cache write failures
    }
  }

  private normalizeContactInfo(value: any): SubscriptionContactInfo {
    const asList = (items: any): string[] => Array.isArray(items)
      ? items.map((item) => `${item ?? ''}`.trim()).filter((item) => item.length > 0)
      : [];

    return {
      mobileWhatsapp: asList(value?.mobileWhatsapp),
      email: asList(value?.email)
    };
  }

  private normalizePackages(value: any, excludeCustom = false): SubscriptionPackage[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item) => ({
      id: Number(item?.id ?? 0),
      packageName: `${item?.packageName ?? ''}`.trim(),
      totalFlat: Number(item?.totalFlat ?? 0),
      totalRenter: Number(item?.totalRenter ?? 0),
      maxActiveEmployee: Number(item?.maxActiveEmployee ?? 0),
      maxFileMemory: Number(item?.maxFileMemory ?? 0),
      priceMonthly: Number(item?.priceMonthly ?? 0),
      priceYearly: Number(item?.priceYearly ?? 0),
      status: Number(item?.status ?? 0)
    })).filter((item) => item.id > 0
      && item.packageName.length > 0
      && (!excludeCustom || item.packageName.trim().toLowerCase() !== 'custom'));
  }

  private normalizePackageHistory(value: any): SubscriptionPackageHistoryItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item) => ({
      id: Number(item?.id ?? 0),
      packageId: Number(item?.packageId ?? 0),
      packageName: `${item?.packageName ?? ''}`.trim(),
      totalFlat: Number(item?.totalFlat ?? 0),
      totalRenter: Number(item?.totalRenter ?? 0),
      maxActiveEmployee: Number(item?.maxActiveEmployee ?? 0),
      maxFileMemory: Number(item?.maxFileMemory ?? 0),
      priceMonthly: Number(item?.priceMonthly ?? 0),
      priceYearly: Number(item?.priceYearly ?? 0),
      status: Number(item?.status ?? 0),
      actionStatus: `${item?.actionStatus ?? ''}`.trim(),
      createdBy: Number(item?.createdBy ?? 0),
      createdOn: `${item?.createdOn ?? ''}`.trim() || undefined
    })).filter((item) => item.id > 0);
  }

  private normalizeAssignPackages(value: any): AssignPackageRecord[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item) => ({
      id: Number(item?.id ?? 0),
      userId: Number(item?.userId ?? 0),
      packageName: `${item?.packageName ?? ''}`.trim(),
      price: Number(item?.price ?? 0),
      maxActiveFlat: Number(item?.maxActiveFlat ?? 0),
      maxActiveRenter: Number(item?.maxActiveRenter ?? 0),
      maxActiveEmployee: Number(item?.maxActiveEmployee ?? 0),
      maxFileStorage: Number(item?.maxFileStorage ?? 0),
      isTrial: Boolean(item?.isTrial ?? false),
      remarks: `${item?.remarks ?? ''}`.trim() || undefined,
      expireDate: `${item?.expireDate ?? ''}`.trim() || undefined,
      durationUnite: Number(item?.durationUnite ?? 0),
      durationQuantity: Number(item?.durationQuantity ?? 0),
      status: Number(item?.status ?? 0),
      createdBy: Number(item?.createdBy ?? 0) || undefined,
      createdOn: `${item?.createdOn ?? ''}`.trim() || undefined,
      updatedBy: Number(item?.updatedBy ?? 0) || undefined,
      updatedOn: `${item?.updatedOn ?? ''}`.trim() || undefined,
      appUser: this.normalizeAssignPackageUser(item?.appUser),
      createdByUser: this.normalizeAssignPackageUser(item?.createdByUser),
      updatedByUser: this.normalizeAssignPackageUser(item?.updatedByUser)
    })).filter((item) => item.id > 0);
  }

  private normalizeAssignPackageHistory(value: any): AssignPackageHistoryItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item) => ({
      id: Number(item?.id ?? 0),
      assignPackageId: Number(item?.assignPackageId ?? 0),
      userId: Number(item?.userId ?? 0),
      packageName: `${item?.packageName ?? ''}`.trim(),
      price: Number(item?.price ?? 0),
      maxActiveFlat: Number(item?.maxActiveFlat ?? 0),
      maxActiveRenter: Number(item?.maxActiveRenter ?? 0),
      maxActiveEmployee: Number(item?.maxActiveEmployee ?? 0),
      maxFileStorage: Number(item?.maxFileStorage ?? 0),
      isTrial: Boolean(item?.isTrial ?? false),
      remarks: `${item?.remarks ?? ''}`.trim() || undefined,
      expireDate: `${item?.expireDate ?? ''}`.trim() || undefined,
      durationUnite: Number(item?.durationUnite ?? 0),
      durationQuantity: Number(item?.durationQuantity ?? 0),
      status: Number(item?.status ?? 0),
      actionStatus: `${item?.actionStatus ?? ''}`.trim() || undefined,
      createdBy: Number(item?.createdBy ?? 0) || undefined,
      createdOn: `${item?.createdOn ?? ''}`.trim() || undefined,
      updatedBy: Number(item?.updatedBy ?? 0) || undefined,
      appUser: this.normalizeAssignPackageUser(item?.appUser),
      createdByUser: this.normalizeAssignPackageUser(item?.createdByUser),
      updatedByUser: this.normalizeAssignPackageUser(item?.updatedByUser)
    })).filter((item) => item.id > 0);
  }

  private normalizeAssignPackageUser(value: any): AssignPackageRecord['appUser'] | undefined {
    const id = Number(value?.id ?? 0);
    if (!id) {
      return undefined;
    }

    return {
      id,
      name: `${value?.name ?? ''}`.trim() || undefined,
      organizationName: `${value?.organizationName ?? value?.organization_name ?? ''}`.trim() || undefined,
      mobileNumber: `${value?.mobileNumber ?? ''}`.trim() || undefined,
      email: `${value?.email ?? ''}`.trim() || undefined,
      username: `${value?.username ?? ''}`.trim() || undefined,
      role: Number(value?.role ?? 0) || undefined
    };
  }
  private hasAnyContact(value: SubscriptionContactInfo | null | undefined): boolean {
    return Number(value?.mobileWhatsapp?.length ?? 0) > 0 || Number(value?.email?.length ?? 0) > 0;
  }

  private normalizeSubscriptionSummary(value: any): SubscriptionUsageSummary {
    return {
      accountId: Number(value?.accountId ?? 0),
      currentPackage: {
        id: Number(value?.currentPackage?.id ?? 0) || undefined,
        userId: Number(value?.currentPackage?.userId ?? 0) || undefined,
        packageName: `${value?.currentPackage?.packageName ?? ''}`.trim() || undefined,
        price: Number(value?.currentPackage?.price ?? 0),
        maxActiveFlat: Number(value?.currentPackage?.maxActiveFlat ?? 0),
        maxActiveRenter: Number(value?.currentPackage?.maxActiveRenter ?? 0),
        maxActiveEmployee: Number(value?.currentPackage?.maxActiveEmployee ?? 0),
        maxFileStorage: Number(value?.currentPackage?.maxFileStorage ?? 0),
        isTrial: Boolean(value?.currentPackage?.isTrial ?? false),
        expireDate: `${value?.currentPackage?.expireDate ?? ''}`.trim() || undefined,
        durationUnite: Number(value?.currentPackage?.durationUnite ?? 0) || undefined,
        durationQuantity: Number(value?.currentPackage?.durationQuantity ?? 0) || undefined,
        remarks: `${value?.currentPackage?.remarks ?? ''}`.trim() || undefined
      },
      usage: {
        activeFlatCount: Number(value?.usage?.activeFlatCount ?? 0),
        activeRenterCount: Number(value?.usage?.activeRenterCount ?? 0),
        activeEmployeeCount: Number(value?.usage?.activeEmployeeCount ?? 0),
        usedFileSize: Number(value?.usage?.usedFileSize ?? 0)
      }
    };
  }
}
